import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Card, Table, Button, Switch, Space, Tag, Tooltip, Modal, Form,
  Input, Select, InputNumber, Row, Col, Typography, Divider,
  notification, Popconfirm, Badge, Collapse,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, PlayCircleOutlined,
  ClockCircleOutlined, TeamOutlined, DollarOutlined, MessageOutlined,
} from '@ant-design/icons';
import { baseUrl } from '../../config';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
});

const TRIGGER_LABELS = {
  joining_day: 'Joining Day',
  fixed_day:   'Fixed Day of Month',
  every_day:   'Every Day',
};

const PAYMENT_CONDITION_LABELS = {
  overdue:          { label: 'Any Overdue',         color: 'orange' },
  consecutive_miss: { label: 'Consecutive Months Missed', color: 'red' },
  never_paid:       { label: 'Never Paid',           color: 'volcano' },
  all_pending:      { label: 'All Pending',          color: 'gold' },
};

const STATUS_COLORS = { Active: 'green', Inactive: 'default', All: 'blue' };

export default function PaymentReminderRulesPage() {
  const [rules, setRules]             = useState([]);
  const [loading, setLoading]         = useState(false);
  const [modalOpen, setModalOpen]     = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving]           = useState(false);
  const [runningId, setRunningId]     = useState(null);

  // Dropdown options fetched from server
  const [villages, setVillages]     = useState([]);
  const [districts, setDistricts]   = useState([]);
  const [tehsils, setTehsils]       = useState([]);
  const [accounts, setAccounts]     = useState([]);

  const [form] = Form.useForm();
  const triggerOn      = Form.useWatch('trigger_on', form);
  const paymentCond    = Form.useWatch('payment_condition', form);

  // ── Load rules ──────────────────────────────────────────────────────────────
  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/payment-reminder-rules/list`, { headers: authHeaders() });
      if (res.data.status) setRules(res.data.result || []);
    } catch (err) {
      notification.error({ message: 'Failed to load rules: ' + err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Load dropdown options ────────────────────────────────────────────────────
  const loadDropdowns = useCallback(async () => {
    try {
      const [vRes, dRes, tRes, aRes] = await Promise.all([
        axios.get(`${baseUrl}/pop-village/list-all`, { headers: authHeaders() }),
        axios.get(`${baseUrl}/pop-districts/list-all`, { headers: authHeaders() }),
        axios.get(`${baseUrl}/pop-tehsils/list-all`, { headers: authHeaders() }),
        axios.get(`${baseUrl}/bank-account/active`, { headers: authHeaders() }),
      ]);
      if (vRes.data.status) setVillages(vRes.data.result || []);
      if (dRes.data.status) setDistricts(dRes.data.result || []);
      if (tRes.data.status) setTehsils(tRes.data.result || []);
      if (aRes.data.status) setAccounts(aRes.data.result || []);
    } catch (_) {}
  }, []);

  useEffect(() => { loadRules(); loadDropdowns(); }, [loadRules, loadDropdowns]);

  // ── Toggle active / inactive ────────────────────────────────────────────────
  const toggleActive = async (id, checked) => {
    try {
      await axios.patch(`${baseUrl}/payment-reminder-rules/${id}`, { is_active: checked }, { headers: authHeaders() });
      setRules(prev => prev.map(r => r.id === id ? { ...r, is_active: checked ? 1 : 0 } : r));
    } catch (err) {
      notification.error({ message: 'Toggle failed: ' + err.message });
    }
  };

  // ── Run now ─────────────────────────────────────────────────────────────────
  const runNow = async (id, name) => {
    setRunningId(id);
    try {
      const res = await axios.post(`${baseUrl}/payment-reminder-rules/${id}/run-now`, {}, { headers: authHeaders() });
      if (res.data.status) {
        notification.success({ message: `"${name}" executed successfully.` });
        loadRules();
      } else {
        notification.error({ message: res.data.message });
      }
    } catch (err) {
      notification.error({ message: 'Run failed: ' + err.message });
    } finally {
      setRunningId(null);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────
  const deleteRule = async (id) => {
    try {
      await axios.delete(`${baseUrl}/payment-reminder-rules/${id}`, { headers: authHeaders() });
      notification.success({ message: 'Rule deleted.' });
      loadRules();
    } catch (err) {
      notification.error({ message: 'Delete failed: ' + err.message });
    }
  };

  // ── Open form ────────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingRule(null);
    form.resetFields();
    form.setFieldsValue({
      trigger_on: 'joining_day', member_status: 'Active',
      payment_condition: 'overdue', consecutive_months: 2,
      template_params: ['name'], is_active: true,
    });
    setModalOpen(true);
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    form.setFieldsValue({
      rule_name:          rule.rule_name,
      description:        rule.description || '',
      trigger_on:         rule.trigger_on,
      fixed_day:          rule.fixed_day,
      member_status:      rule.member_status,
      village_filter:     Array.isArray(rule.village_filter)  ? rule.village_filter  : [],
      district_filter:    Array.isArray(rule.district_filter) ? rule.district_filter : [],
      tehsil_filter:      Array.isArray(rule.tehsil_filter)   ? rule.tehsil_filter   : [],
      payment_condition:  rule.payment_condition,
      consecutive_months: rule.consecutive_months || 2,
      campaign_name:      rule.campaign_name || '',
      template_params:    Array.isArray(rule.template_params) ? rule.template_params : ['name'],
      poster_key:         rule.poster_key || '',
      is_active:          rule.is_active === 1,
      bank_account_id:    rule.bank_account_id || null,
    });
    setModalOpen(true);
  };

  // ── Save ─────────────────────────────────────────────────────────────────────
  const onSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch (_) { return; }

    setSaving(true);
    try {
      const url    = editingRule
        ? `${baseUrl}/payment-reminder-rules/${editingRule.id}`
        : `${baseUrl}/payment-reminder-rules/create`;
      const method = editingRule ? 'patch' : 'post';

      const res = await axios[method](url, values, { headers: authHeaders() });
      if (res.data.status) {
        notification.success({ message: editingRule ? 'Rule updated.' : 'Rule created.' });
        setModalOpen(false);
        loadRules();
      } else {
        notification.error({ message: res.data.message });
      }
    } catch (err) {
      notification.error({ message: 'Save failed: ' + err.message });
    } finally {
      setSaving(false);
    }
  };

  // ── Table columns ────────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Rule Name',
      dataIndex: 'rule_name',
      render: (v, r) => (
        <div>
          <Text strong>{v}</Text>
          {r.description && <div><Text type="secondary" style={{ fontSize: 8 }}>{r.description}</Text></div>}
        </div>
      ),
    },
    {
      title: 'When',
      dataIndex: 'trigger_on',
      width: 160,
      render: (v, r) => (
        <div>
          <Tag color="blue" icon={<ClockCircleOutlined />}>{TRIGGER_LABELS[v] || v}</Tag>
          {v === 'fixed_day' && r.fixed_day && (
            <div><Text type="secondary" style={{ fontSize: 11 }}>Day {r.fixed_day} of month</Text></div>
          )}
        </div>
      ),
    },
    {
      title: 'Who',
      width: 160,
      render: (_, r) => (
        <div>
          <Tag color={STATUS_COLORS[r.member_status] || 'default'} icon={<TeamOutlined />}>{r.member_status}</Tag>
          {r.bank_account_id && (
            <div style={{ marginTop: 2 }}>
              <Tag color="purple" style={{ fontSize: 10 }}>
                {accounts.find(a => a.id === r.bank_account_id)?.account_name || `Acct #${r.bank_account_id}`}
              </Tag>
            </div>
          )}
          {(r.village_filter?.length > 0 || r.district_filter?.length > 0 || r.tehsil_filter?.length > 0) && (
            <div style={{ marginTop: 2 }}>
              {r.village_filter?.length  > 0 && <Tag style={{ fontSize: 10 }}>{r.village_filter.length} village(s)</Tag>}
              {r.district_filter?.length > 0 && <Tag style={{ fontSize: 10 }}>{r.district_filter.length} district(s)</Tag>}
              {r.tehsil_filter?.length   > 0 && <Tag style={{ fontSize: 10 }}>{r.tehsil_filter.length} tehsil(s)</Tag>}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Payment Condition',
      width: 200,
      render: (_, r) => {
        const c = PAYMENT_CONDITION_LABELS[r.payment_condition] || {};
        return (
          <div>
            <Tag color={c.color} icon={<DollarOutlined />}>{c.label || r.payment_condition}</Tag>
            {r.payment_condition === 'consecutive_miss' && (
              <Text type="secondary" style={{ fontSize: 11 }}> {r.consecutive_months} months</Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Campaign',
      dataIndex: 'campaign_name',
      width: 140,
      render: v => v ? <Tag icon={<MessageOutlined />}>{v}</Tag> : <Text type="secondary" style={{ fontSize: 11 }}>—</Text>,
    },
    {
      title: 'Last Run',
      width: 150,
      render: (_, r) => (
        <div>
          {r.last_run
            ? <Text style={{ fontSize: 11 }}>{new Date(r.last_run).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</Text>
            : <Text type="secondary" style={{ fontSize: 11 }}>Never</Text>}
          {r.last_status && (
            <div>
              <Text style={{ fontSize: 10, color: r.last_status.startsWith('Error') ? '#ff4d4f' : '#52c41a' }}>
                {r.last_status.slice(0, 40)}{r.last_status.length > 40 ? '…' : ''}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      width: 70,
      render: (v, r) => (
        <Switch size="small" checked={v === 1} onChange={(checked) => toggleActive(r.id, checked)} />
      ),
    },
    {
      title: 'Actions',
      width: 130,
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          </Tooltip>
          <Tooltip title="Run Now">
            <Button
              size="small" type="primary" ghost icon={<PlayCircleOutlined />}
              loading={runningId === r.id}
              onClick={() => runNow(r.id, r.rule_name)}
            />
          </Tooltip>
          <Popconfirm title="Delete this rule?" onConfirm={() => deleteRule(r.id)} okText="Delete" okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        bordered={false}
        headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
        style={{ backgroundColor: '#fff', borderRadius: 8 }}
        bodyStyle={{ padding: 16 }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>Payment Reminder Rules</span>
              <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, fontWeight: 400, marginTop: 2 }}>
                Define who gets a payment reminder, when, and what message to send. The daily cron evaluates all active rules.
              </div>
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} size="small">Add Rule</Button>
          </div>
        }
      >
        <Table
          dataSource={rules}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="middle"
          locale={{ emptyText: 'No rules yet. Click "Add Rule" to create one.' }}
        />
      </Card>

      {/* ── Add / Edit Modal ─────────────────────────────────────────────────── */}
      <Modal
        open={modalOpen}
        title={editingRule ? `Edit Rule — ${editingRule.rule_name}` : 'New Payment Reminder Rule'}
        onCancel={() => setModalOpen(false)}
        onOk={onSave}
        okText={editingRule ? 'Update' : 'Create'}
        confirmLoading={saving}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" size="small" style={{ marginTop: 8 }}>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="rule_name" label="Rule Name" rules={[{ required: true, message: 'Required' }]}>
                <Input placeholder="e.g. Joining Day – Overdue Members" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description (optional)">
            <Input placeholder="Brief description of this rule" />
          </Form.Item>

          <Divider orientation="left" style={{ fontSize: 12, color: '#1890ff' }}>
            <ClockCircleOutlined /> When to Fire
          </Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="trigger_on" label="Trigger On" rules={[{ required: true }]}>
                <Select>
                  <Option value="joining_day">Joining Day — member's monthly anniversary</Option>
                  <Option value="fixed_day">Fixed Day of Month</Option>
                  <Option value="every_day">Every Day the cron runs</Option>
                </Select>
              </Form.Item>
            </Col>
            {triggerOn === 'fixed_day' && (
              <Col span={12}>
                <Form.Item name="fixed_day" label="Day of Month (1–31)" rules={[{ required: true, message: 'Required when trigger is Fixed Day' }]}>
                  <InputNumber min={1} max={31} style={{ width: '100%' }} placeholder="e.g. 5" />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Divider orientation="left" style={{ fontSize: 12, color: '#1890ff' }}>
            <TeamOutlined /> Who to Target
          </Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="bank_account_id" label="Bank Account (Payment Scope)">
                <Select allowClear placeholder="All accounts (no filter)">
                  {accounts.map(a => (
                    <Option key={a.id} value={a.id}>{a.account_name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="member_status" label="Member Status">
                <Select>
                  <Option value="Active">Active members only</Option>
                  <Option value="Inactive">Inactive members only</Option>
                  <Option value="All">All members (Active + Inactive)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Collapse ghost size="small">
            <Panel header={<Text style={{ fontSize: 12 }}>Geographic Filters (Village / District / Tehsil) — leave empty to target all</Text>} key="geo">
              <Form.Item name="village_filter" label="Villages (select specific villages, or leave empty for all)">
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  placeholder="All villages"
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                >
                  {villages.map(v => (
                    <Option key={v.id} value={v.name_hn || v.name_en || v.name}>{v.name_hn || v.name_en || v.name}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="district_filter" label="Districts">
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  placeholder="All districts"
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                >
                  {districts.map(d => (
                    <Option key={d.id} value={d.name_hn || d.name_en || d.name}>{d.name_hn || d.name_en || d.name}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="tehsil_filter" label="Tehsils / Post">
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  placeholder="All tehsils"
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                >
                  {tehsils.map(t => (
                    <Option key={t.id} value={t.name_hn || t.name_en || t.name}>{t.name_hn || t.name_en || t.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Panel>
          </Collapse>

          <Divider orientation="left" style={{ fontSize: 12, color: '#1890ff' }}>
            <DollarOutlined /> Payment Condition
          </Divider>

          <Row gutter={16}>
            <Col span={paymentCond === 'consecutive_miss' ? 14 : 24}>
              <Form.Item name="payment_condition" label="Condition" rules={[{ required: true }]}>
                <Select>
                  <Option value="overdue">Any Overdue — last payment is before this month</Option>
                  <Option value="consecutive_miss">Consecutive Months Missed — N or more months unpaid</Option>
                  <Option value="never_paid">Never Paid — no payment records at all</Option>
                  <Option value="all_pending">All Pending — same as overdue (broad match)</Option>
                </Select>
              </Form.Item>
            </Col>
            {paymentCond === 'consecutive_miss' && (
              <Col span={10}>
                <Form.Item name="consecutive_months" label="Months Missed (minimum)">
                  <InputNumber min={1} max={24} style={{ width: '100%' }} addonAfter="months" />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Divider orientation="left" style={{ fontSize: 12, color: '#1890ff' }}>
            <MessageOutlined /> WhatsApp Message
          </Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="campaign_name" label="Campaign / Template Name">
                <Input placeholder="e.g. daysamarpan" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="poster_key" label="Poster Key (optional — overrides rules engine)">
                <Input placeholder="e.g. payment_reminder" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="template_params" label="Template Params (fields sent to WhatsApp template)">
            <Select
              mode="tags"
              placeholder="Add fields e.g. name, village, district"
              tokenSeparators={[',']}
            >
              {['name', 'village', 'district', 'phone', 'father_name', 'tehsil', 'address'].map(f => (
                <Option key={f} value={f}>{f}</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
